import * as React from 'react'
import { Accordion as AccordionPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Accordion` primitive, from the new-york registry via the CLI
 * (`npx shadcn@latest add accordion`). Backs the `src/antd/Collapse` seam's
 * shadcn half (`src/antd/Collapse/Collapse.shadcn.tsx`); nothing else should
 * import it, and nothing reaches it until someone flips the design toggle.
 *
 * Radix ships as the `radix-ui` monopackage here, which already carries
 * `Accordion` — so this port added no dependency.
 *
 * FIVE EDITS TO THE REGISTRY OUTPUT, each forced by this branch:
 *
 * 1. `AccordionHeader` IS EXPORTED. The registry folds the header into
 *    `AccordionTrigger`, but antd's Collapse header holds `extra` BESIDE the
 *    clickable part, and `extra` is a `<Button>` at its one call site
 *    (`ConfigurationTab`) — a button inside a button is invalid markup and the
 *    inner one would not receive its own click. The seam therefore composes
 *    the header itself and needs the part.
 *
 * 2. NO BUILT-IN CHEVRON. antd's Collapse takes `expandIcon` (6 call sites
 *    override it, two of them with `() => null`) and `expandIconPosition`
 *    (15 call sites ask for `end`), so the glyph is the seam's to render, not
 *    this file's. The registry's `[&[data-state=open]>svg]:rotate-180` and its
 *    `ChevronDownIcon` import go with it — which also removes the raw
 *    `lucide-react` import the CLI wrote, that AGENTS.md forbids outside
 *    `src/icons/antd-compat` and
 *    `src/antd/__tests__/seam-adoption.evidence.test.tsx` counts.
 *
 * 3. THE HEADER'S TYPOGRAPHY IS NEUTRALISED. Radix renders `Primitive.h3`,
 *    and this app imports no Preflight (see `src/styles/tailwind.css`), so
 *    Chrome's UA sheet would give every collapse header `1.17em`, `bold` and
 *    a `1em` block margin. Preflight's heading reset is not re-emitted
 *    app-wide for that — it would have to be scoped to `[data-slot]` like the
 *    rest of that layer and would then reach every ported heading — so the
 *    three declarations are utilities on this part instead.
 *
 * 4. NO PADDING, NO BORDER, NO TYPE SCALE ON THE PARTS. The registry's
 *    `border-b`, `py-4`, `pt-0 pb-4` and `text-sm` are antd's `headerPadding`
 *    / `contentPadding` / panel border in a different place, and the seam
 *    reproduces antd's own metrics per `size`/`ghost`/`bordered` instead —
 *    ~15 stylesheets in this repo override exactly those, so they may only be
 *    declared once.
 *
 * 5. `AccordionContent` HIDES ITSELF WHEN CLOSED. rc-collapse keeps a panel
 *    mounted once it has been opened and hides it with `display: none`
 *    (`.ant-collapse-content-hidden`); the seam reproduces that with
 *    `forceMount`, and Radix computes `hidden = !(forceMount || open)`, so
 *    `forceMount` alone would leave a closed panel's body on screen. The
 *    `hidden` attribute is applied by the seam, after the exit animation —
 *    `data-[state=closed]:h-0` is what holds the box shut in between.
 * ------------------------------------------------------------------------ */

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentProps<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot="accordion-item"
    className={className}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionHeader = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Header>,
  React.ComponentProps<typeof AccordionPrimitive.Header>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Header
    ref={ref}
    data-slot="accordion-header"
    className={cn(
      'm-0 flex [font-size:inherit] [font-weight:inherit] [line-height:inherit]',
      className
    )}
    {...props}
  />
))
AccordionHeader.displayName = 'AccordionHeader'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentProps<typeof AccordionPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Trigger
    ref={ref}
    data-slot="accordion-trigger"
    className={cn(
      'flex flex-1 cursor-pointer items-start text-left outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  />
))
AccordionTrigger.displayName = 'AccordionTrigger'

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentProps<typeof AccordionPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-slot="accordion-content"
    className={cn(
      'overflow-hidden data-[state=closed]:h-0 data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down motion-reduce:animate-none',
      className
    )}
    {...props}
  />
))
AccordionContent.displayName = 'AccordionContent'

export {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionContent,
}
